import React, { Suspense, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ShaderGradientCanvas, ShaderGradient } from '@shadergradient/react';
import HubBackground from '../effects/HubBackground';
import { getCtaShaderProps } from '../effects/shaderGradientPresets.js';
import { useProducts, getProductIcon, getProductPriceInfo } from '../../../hooks/useProducts.jsx';
import { useIsCompactLayout } from '../../../hooks/useIsCompactLayout.js';
import { useInfiniteCarouselScroll } from '../../../hooks/useInfiniteCarouselScroll.js';
import { useCarouselActiveIndex } from '../../../hooks/useCarouselActiveIndex.js';
import CarouselIndicator from '../CarouselIndicator.jsx';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { usePanelRevealObserver } from '../../../hooks/usePanelRevealObserver.js';

/**
 * 料金ページ
 */
const PricingPage = () => {
  const isMobile = useIsCompactLayout();
  const [activeSection, setActiveSection] = useState('content');
  const contentRef = useRef(null);
  const faqRef = useRef(null);
  const ctaRef = useRef(null);
  const carouselRef = useRef(null);

  // CMSから製品データを取得
  const { products, loading: productsLoading } = useProducts();

  useInfiniteCarouselScroll(carouselRef, products.length, isMobile && products.length > 0);
  const carouselActiveIndex = useCarouselActiveIndex(carouselRef, products.length, isMobile && products.length > 0);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-40% 0px -40% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.dataset.section);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    if (contentRef.current) observer.observe(contentRef.current);
    if (faqRef.current) observer.observe(faqRef.current);
    if (ctaRef.current) observer.observe(ctaRef.current);

    return () => observer.disconnect();
  }, []);

  // Sync activeSection with header styling via body class
  useEffect(() => {
    if (activeSection === 'content') {
      document.body.classList.add('pricing-hero-active');
    } else {
      document.body.classList.remove('pricing-hero-active');
    }
    return () => {
      document.body.classList.remove('pricing-hero-active');
    };
  }, [activeSection]);

  usePanelRevealObserver('.hub-pricing .hub-panel-animated', [products.length], '500px 0px -5% 0px');

  // Enterpriseプラン（問い合わせ）
  const enterprisePlan = {
    id: 'enterprise',
    name: 'Enterprise',
    description: '複数拠点運営やグループ向けの包括的ソリューション',
    features: [
      '全モジュール利用可能',
      'ユーザー数無制限',
      'データ保存無制限',
      '24/7専用サポート',
      'SLA保証',
      '専任カスタマーサクセス',
      'オンボーディング支援',
      'カスタム開発対応'
    ]
  };

  return (
    <div className="hub-pricing-page">
      {/* Fixed Gradient Backgrounds */}
      <div className="hub-fixed-gradient-container">
        {/* BG00 - Hero (ColorBends) - Content Section / BG00_mobil = Iridescence on mobile */}
        <div className={`hub-fixed-gradient-layer ${activeSection === 'content' ? 'active' : ''}`}>
          <HubBackground isMobile={isMobile} />
        </div>

        {/* BG03 - Modules (FAQ Section) */}
        <div className={`hub-fixed-gradient-layer ${activeSection === 'faq' ? 'active' : ''}`}>
          <ShaderGradientCanvas
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            <Suspense fallback={null}>
              <ShaderGradient
                animate="on"
                axesHelper="off"
                bgColor1="#000000"
                bgColor2="#000000"
                brightness={1.2}
                cAzimuthAngle={180}
                cDistance={2.5}
                cPolarAngle={120}
                cameraZoom={1}
                color1="#ebedff"
                color2="#f3f2f8"
                color3="#dbf8ff"
                destination="onCanvas"
                embedMode="off"
                envPreset="city"
                format="gif"
                fov={45}
                frameRate={10}
                gizmoHelper="hide"
                grain="off"
                lightType="3d"
                pixelDensity={1}
                positionX={0}
                positionY={1.8}
                positionZ={0}
                range="disabled"
                rangeEnd={40}
                rangeStart={0}
                reflection={0.1}
                rotationX={0}
                rotationY={0}
                rotationZ={-90}
                shader="defaults"
                type="waterPlane"
                uAmplitude={0}
                uDensity={1.3}
                uFrequency={5.5}
                uSpeed={0.1}
                uStrength={3}
                uTime={0.2}
                wireframe={false}
                zoomOut={false}
              />
            </Suspense>
          </ShaderGradientCanvas>
        </div>

        {/* BG01 - CTA / BG01_mobil on mobile */}
        <div className={`hub-fixed-gradient-layer ${activeSection === 'cta' ? 'active' : ''}`}>
          <ShaderGradientCanvas
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            <Suspense fallback={null}>
              <ShaderGradient {...getCtaShaderProps(isMobile)} />
            </Suspense>
          </ShaderGradientCanvas>
        </div>
      </div>

      {/* ヒーロー */}
      <section 
        className="hub-hero-no-bg"
        style={{ 
          paddingTop: 'var(--hub-space-5xl)',
          paddingBottom: 'var(--hub-space-lg)',
          paddingLeft: 'var(--hub-space-xl)',
          paddingRight: 'var(--hub-space-xl)',
          textAlign: 'center',
          position: 'relative',
          zIndex: 2
        }}
      >
        <div style={{ maxWidth: 'var(--hub-max-width)', margin: '0 auto' }}>
          <div className="hub-section-badge hub-hero-badge" style={{ background: 'rgba(99, 102, 241, 0.15)', color: 'var(--hub-accent-primary)' }}>
            Pricing
          </div>
          <h1 className="hub-hero-title" style={{ 
            fontFamily: 'var(--hub-font-display)', 
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 800,
            marginBottom: 'var(--hub-space-md)',
            color: '#fff',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.9), 0 4px 16px rgba(0, 0, 0, 0.7), 0 8px 32px rgba(0, 0, 0, 0.5)'
          }}>
            課題に応じた必要な機能を、<br />自由にカスタマイズ
          </h1>
          <p className="hub-hero-subtitle" style={{ 
            fontSize: '1.125rem', 
            color: '#fff',
            maxWidth: '700px',
            margin: '0 auto',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.9), 0 4px 16px rgba(0, 0, 0, 0.7)'
          }}>
            各モジュールを個別に契約できるシンプルな料金体系。<br />
            必要な機能だけを選んで、無駄なくご利用いただけます。
          </p>
        </div>
      </section>

      {/* モジュール別料金 */}
      <section 
        ref={contentRef}
        data-section="content"
        className="hub-pricing hub-section-dark"
        style={{ position: 'relative', zIndex: 1, background: 'transparent' }}
      >
        <div className="hub-pricing-inner">
          {/* セクションヘッダー */}
          <div className="hub-section-header" style={{ marginBottom: 'var(--hub-space-2xl)' }}>
            <h2 className="hub-section-title">モジュール料金一覧</h2>
            <p className="hub-section-subtitle">
              必要なモジュールを個別に契約できます
            </p>
          </div>

          {productsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--hub-space-3xl)' }}>
              <LoadingSpinner size="large" />
            </div>
          ) : (
            <>
              {isMobile && products.length > 0 ? (
                <>
                <div ref={carouselRef} className="hub-mobile-panel-carousel" role="region" aria-label="料金一覧">
                  {[
                    ...(products.length > 0 ? [products[products.length - 1]] : []),
                    ...products,
                    ...(products.length > 0 ? [products[0]] : []),
                  ].map((product, index) => {
                    const isClone = index === 0 || index === products.length + 1;
                    const key = isClone ? `carousel-clone-${index === 0 ? 'last' : 'first'}-${product.id}` : product.id;
                    const priceInfo = getProductPriceInfo(product);
                    return (
                      <div key={key} className="hub-mobile-panel-carousel-slide">
                        <div className={`hub-pricing-module-card hub-panel-animated visible ${product.isComingSoon ? 'coming-soon' : ''}`}>
                          <div className="hub-pricing-module-header">
                            <div className="hub-pricing-module-icon">
                              {getProductIcon(product.iconType)}
                            </div>
                            <div className="hub-pricing-module-info">
                              <h3>{product.name}</h3>
                              {product.isComingSoon && <span className="hub-badge hub-badge-warning">準備中</span>}
                            </div>
                          </div>
                          <p className="hub-pricing-module-desc">{product.shortDescription}</p>
                          <div className="hub-pricing-module-price">
                            {priceInfo.isFree ? (
                              <div className="hub-price-free-block">
                                <span className="hub-price-amount hub-price-amount-free">無料</span>
                                {priceInfo.freeNote && <span className="hub-price-note">（{priceInfo.freeNote}）</span>}
                              </div>
                            ) : priceInfo.hasPrice ? (
                              <div className="hub-price-block">
                                <span className="hub-price-amount">¥{priceInfo.monthlyPrice.toLocaleString()}</span>
                                <span className="hub-price-period">/月</span>
                                <span className="hub-price-tax-note">税別</span>
                                {priceInfo.priceNote && (
                                  <span className="hub-price-note">（{priceInfo.priceNote}）</span>
                                )}
                              </div>
                            ) : (
                              <div className="hub-price-free-block">
                                <span className="hub-price-amount">価格未設定</span>
                              </div>
                            )}
                          </div>
                          <ul className="hub-pricing-module-features">
                            {(product.features || []).slice(0, 3).map((feature, idx) => (
                              <li key={idx}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                                {feature}
                              </li>
                            ))}
                          </ul>
                          {!product.isComingSoon ? (
                            <Link to="/signup" className="hub-btn hub-btn-gradient" style={{ width: '100%' }}>無料で試す</Link>
                          ) : (
                            <button className="hub-btn hub-btn-secondary" style={{ width: '100%' }} disabled>Coming Soon</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <CarouselIndicator count={products.length} activeIndex={carouselActiveIndex} />
                </>
              ) : null}
              {/* モジュールカード（デスクトップ） */}
              {!isMobile && (
              <div className="hub-pricing-modules-grid">
                {products.map((product) => {
                  const priceInfo = getProductPriceInfo(product);

                  return (
                    <div 
                      key={product.id} 
                      className={`hub-pricing-module-card hub-panel-animated ${product.isComingSoon ? 'coming-soon' : ''}`}
                    >
                    <div className="hub-pricing-module-header">
                      <div className="hub-pricing-module-icon">
                        {getProductIcon(product.iconType)}
                      </div>
                      <div className="hub-pricing-module-info">
                        <h3>{product.name}</h3>
                        {product.isComingSoon && (
                          <span className="hub-badge hub-badge-warning">準備中</span>
                        )}
                      </div>
                    </div>
                    
                    <p className="hub-pricing-module-desc">
                      {product.shortDescription}
                    </p>
                    
                      <div className="hub-pricing-module-price">
                        {priceInfo.isFree ? (
                          <div className="hub-price-free-block">
                            <span className="hub-price-amount hub-price-amount-free">無料</span>
                            {priceInfo.freeNote && (
                              <span className="hub-price-note">（{priceInfo.freeNote}）</span>
                            )}
                          </div>
                        ) : priceInfo.hasPrice ? (
                          <div className="hub-price-block">
                            <span className="hub-price-amount">¥{priceInfo.monthlyPrice.toLocaleString()}</span>
                            <span className="hub-price-period">/月</span>
                            <span className="hub-price-tax-note">税別</span>
                            {priceInfo.priceNote && (
                              <span className="hub-price-note">（{priceInfo.priceNote}）</span>
                            )}
                          </div>
                        ) : (
                          <div className="hub-price-free-block">
                            <span className="hub-price-amount">価格未設定</span>
                          </div>
                        )}
                      </div>

                    <ul className="hub-pricing-module-features">
                      {(product.features || []).slice(0, 3).map((feature, idx) => (
                        <li key={idx}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {!product.isComingSoon ? (
                      <Link to="/signup" className="hub-btn hub-btn-gradient" style={{ width: '100%' }}>
                        無料で試す
                      </Link>
                    ) : (
                      <button className="hub-btn hub-btn-secondary" style={{ width: '100%' }} disabled>
                        Coming Soon
                      </button>
                    )}
                    </div>
                  );
                })}
              </div>
              )}

              {/* Enterprise プラン */}
              <div className="hub-enterprise-section">
                <div className="hub-enterprise-card hub-panel-animated">
                  <div className="hub-enterprise-content">
                    <div>
                      <h3>{enterprisePlan.name}</h3>
                      <p>{enterprisePlan.description}</p>
                      <ul className="hub-enterprise-features">
                        {enterprisePlan.features.map((feature, index) => (
                          <li key={index}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="hub-enterprise-cta">
                      <div className="hub-enterprise-price">
                        <span>料金</span>
                        <strong>お問い合わせ</strong>
                      </div>
                      <Link to="/contact" className="hub-btn hub-btn-gradient hub-btn-lg">
                        お問い合わせ
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section 
        ref={faqRef}
        data-section="faq"
        className="hub-section-dark"
        style={{ 
          padding: 'var(--hub-space-4xl) var(--hub-space-xl)',
          position: 'relative',
          zIndex: 1,
          background: 'transparent'
        }}
      >
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div className="hub-section-header">
            <div className="hub-section-badge">FAQ</div>
            <h2 className="hub-section-title">よくあるご質問</h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hub-space-md)' }}>
            {[
              {
                q: '無料トライアル期間中に解約した場合、料金は発生しますか？',
                a: 'いいえ、無料トライアル期間中に解約した場合、一切料金は発生しません。期間の経過後は月末までの日割りの料金が請求され、翌月の1日から定額での料金が適用されます。'
              },
              {
                q: 'モジュールの利用変更はいつでもできますか？',
                a: 'はい、いつでも利用の変更が可能です。ライセンスの追加・削除は即時反映され、契約の解除は次の請求サイクルから適用されます。'
              },
              {
                q: 'データのバックアップはどうなっていますか？',
                a: '全データは毎日自動バックアップされ、30日間保持されます。Enterpriseプランでは、より長期間のバックアップやカスタム設定が可能です。'
              },
              {
                q: '既存のシステムからデータを移行できますか？',
                a: 'はい、クリニックの現状に応じてデータ移行をサポートしています。詳細はサポートチームにお問い合わせください。'
              },
              {
                q: '契約期間の縛りはありますか？',
                a: 'いいえ、すべてのモジュールは1ヶ月単位でいつでも解約可能です。解約時の違約金など、ライセンス料以外の費用は一切発生しません。'
              }
            ].map((faq, index) => (
              <div 
                key={index}
                className="hub-module-card"
                style={{ padding: 'var(--hub-space-xl)' }}
              >
                <h4 style={{ 
                  fontWeight: 600,
                  marginBottom: 'var(--hub-space-sm)',
                  color: 'var(--hub-text-primary)'
                }}>
                  {faq.q}
                </h4>
                <p style={{ 
                  color: 'var(--hub-text-secondary)',
                  lineHeight: 1.6,
                  margin: 0
                }}>
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section 
        ref={ctaRef}
        data-section="cta"
        className="hub-cta"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <div className="hub-cta-inner">
          <h2 className="hub-cta-title">
            まずは無料で始めましょう
          </h2>
          <p className="hub-cta-subtitle">
            14日間すべての機能をご利用いただけます。
            お気軽にお試しください。
          </p>
          <div className="hub-cta-actions">
            <Link to="/signup" className="hub-btn hub-btn-gradient hub-btn-lg">
              無料トライアルを開始
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;

